import { lazy } from 'react';
import ProtectedRoute from '#client/components/ProtectedRoute';
import MainLayout from '#client/components/layout/MainLayout/MainLayout';
import Dashboard from '#client/pages/Dashboard/Dashboard';
import ManualVoucherDistribution from '#client/pages/VoucherDistribution/ManualVoucherDistribution';
import VoucherList from '#client/pages/VoucherDashboard/VoucherList';
import PurchaseOrdersDashboard from '#client/pages/PurchaseOrders/PurchaseOrdersDashboard';
import PurchaseOrderDetail from '#client/pages/PurchaseOrders/PurchaseOrderDetail/PurchaseOrderDetail';
import PurchaseOrderAsn from '#client/pages/PurchaseOrders/PurchaseOrderAsn/PurchaseOrderAsn';
import PurchaseOrderReceivingDocument from '#client/pages/PurchaseOrders/PurchaseOrderReceivingDocument/PurchaseOrderReceivingDocument';
import AdminSettings from '#client/pages/AdminSettings/AdminSettings';
import ProgramPage from '#client/pages/Programs/Program';
import InventoryAdjustmentDetail from '#client/pages/InventoryAdjustments/Detail/InventoryAdjustmentDetail';
import InventoryAdjustmentsPage from '#client/pages/InventoryAdjustments/InventoryAdjustmentsPage';
import TransfersDashboard from '#client/pages/Transfers/TransfersDashboard';
import TransferPage from '#client/pages/Transfers/Transfer';
import Jobs from '#client/pages/Jobs';
import Notifications from '#client/pages/Notifications';
import { Outlet } from 'react-router';
import CreateCycleCount from '#client/pages/CycleCounts/Create';
import CycleCountsDashboard from '#client/pages/CycleCounts';
import CycleCountPerform from '#client/pages/CycleCounts/Perform';
import CycleCountDetail from '#client/pages/CycleCounts/Detail';
import PrintLabels from '#client/pages/PrintLabels/PrintLabels';
import Reports from '#client/pages/Reports/Reports';
import { RouteLoadingFallback } from '#client/components/common/RouteLoader';

// Lazy-loaded route components
const ApiUsers = lazy(() => import('#client/pages/ApiUsers/ApiUsers'));
const ProgramManagementDashboard = lazy(() => import('#client/pages/Programs/ProgramManagementDashboard/ProgramManagementDashboard'));

// Route loaders
import { apiUsersLoader } from '#client/routes/loaders/apiUsersLoader';
import { programDashboardLoader } from '#client/routes/loaders/programDashboardLoader';

const protectedRoutes = [
    {
        path: '/',
        element: (
            <ProtectedRoute>
                <MainLayout />
            </ProtectedRoute>
        ),
        children: [
            { path: 'dashboard', element: <Dashboard />, handle: { title: 'Dashboard' } },
            {
                path: 'reports',
                handle: { title: 'Reports' },
                element: (
                    <ProtectedRoute requiredPermission="reportsView">
                        <Reports />
                    </ProtectedRoute>
                ),
            },
            {
                path: 'settings',
                handle: { title: 'Settings' },
                element: (
                    <ProtectedRoute>
                        <AdminSettings />
                    </ProtectedRoute>
                ),
            },
            {
                path: 'api-users',
                element: (
                    <ProtectedRoute requiredPermission="settingApiKeys">
                        <ApiUsers />
                    </ProtectedRoute>
                ),
                handle: { title: 'API Users' },
                loader: apiUsersLoader,
                HydrateFallback: RouteLoadingFallback, // or whatever your loading spinner component is
            },
            {
                path: 'voucher-distribution/:id',
                element: (
                    <ProtectedRoute requiredPermission="programEdit">
                        <ManualVoucherDistribution />
                    </ProtectedRoute>
                ),
                handle: { title: 'Voucher Distribution', refreshSession: true },
            },
            {
                path: 'voucher-list/:id',
                element: (
                    <ProtectedRoute requiredPermission="programView">
                        <VoucherList />
                    </ProtectedRoute>
                ),
                handle: { title: 'Voucher List', refreshSession: true },
            },
            {
                path: 'transfers',
                handle: { title: 'Transfer Management' },
                element: (
                    <ProtectedRoute requiredPermission="transferView">
                        <Outlet />
                    </ProtectedRoute>
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <ProtectedRoute>
                                <TransfersDashboard />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'create',
                        handle: { title: 'Create Transfer' },
                        element: (
                            <ProtectedRoute>
                                <TransferPage />,
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'view/:id',
                        handle: { title: 'View Transfer' },
                        element: (
                            <ProtectedRoute>
                                <TransferPage />,
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'edit/:id',
                        handle: { title: 'Edit Transfer' },
                        element: (
                            <ProtectedRoute>
                                <TransferPage />,
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'receive/:id',
                        handle: { title: 'Receive Transfer' },
                        element: (
                            <ProtectedRoute>
                                <TransferPage />,
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: 'purchase-orders',
                handle: { title: 'Purchase Order Management' },
                element: (
                    <ProtectedRoute requiredPermission="poView">
                        <Outlet />
                    </ProtectedRoute>
                ),
                children: [
                    {
                        index: true,
                        element: <PurchaseOrdersDashboard />,
                    },
                    {
                        path: ':poId',
                        handle: { title: 'Purchase Order Detail' },
                        element: <PurchaseOrderDetail />,
                    },
                    {
                        path: ':poId/advanced-shipping-notice/:asnId',
                        handle: { title: 'Advanced Shipping Notice' },
                        element: <PurchaseOrderAsn />,
                    },
                    {
                        path: ':poId/advanced-shipping-notice/:asnId/receiving-document/:rdId',
                        handle: { title: 'Advanced Shipping Notice' },
                        element: <PurchaseOrderReceivingDocument />,
                    },
                    {
                        path: ':poId/receiving-document/:rdId',
                        handle: { title: 'Advanced Shipping Notice' },
                        element: <PurchaseOrderReceivingDocument />,
                    },
                ],
            },
            {
                path: 'program-management',
                handle: { title: 'Program Management' },
                element: (
                    <ProtectedRoute requiredPermission="programView">
                        <Outlet />
                    </ProtectedRoute>
                ),
                children: [
                    {
                        index: true,
                        element: <ProgramManagementDashboard />,
                        loader: programDashboardLoader,
                        HydrateFallback: RouteLoadingFallback,
                    },
                    {
                        path: 'create',
                        handle: { title: 'Create Program' },
                        element: (
                            <ProtectedRoute requiredPermission="programEdit">
                                <ProgramPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'view/:id',
                        handle: { title: 'View Program' },
                        element: <ProgramPage />,
                    },
                    {
                        path: 'edit/:id',
                        handle: { title: 'Edit Program' },
                        element: (
                            <ProtectedRoute requiredPermission="programEdit">
                                <ProgramPage />
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: 'inventory-adjustments',
                children: [
                    {
                        index: true,
                        handle: { title: 'Inventory Management' },
                        element: (
                            <ProtectedRoute requiredPermission="adjView">
                                <InventoryAdjustmentsPage />,
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: ':id',
                        handle: { title: 'Inventory Adjustment Creation' },
                        element: (
                            <ProtectedRoute requiredPermission="adjCreate">
                                <InventoryAdjustmentDetail />,
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: 'jobs',
                children: [
                    {
                        index: true,
                        element: (
                            <ProtectedRoute requiredPermission="jobsView">
                                <Jobs />
                            </ProtectedRoute>
                        ),
                    },
                    // TO DO: Create job view and creat job view
                ],
            },
            {
                path: 'notifications',
                handle: { title: 'Notifications' },
                element: (
                    <ProtectedRoute>
                        <Notifications />
                    </ProtectedRoute>
                ),
            },
            {
                path: 'print-labels',
                handle: { title: 'Print Labels' },
                element: (
                    <ProtectedRoute>
                        <PrintLabels />
                    </ProtectedRoute>
                ),
            },
            {
                path: 'cycle-counts',
                handle: { title: 'Cycle Count Management' },
                element: (
                    <ProtectedRoute requiredPermission="ccView">
                        <Outlet />
                    </ProtectedRoute>
                ),
                children: [
                    {
                        index: true,
                        handle: { title: 'Cycle Counts Dashboard' },
                        element: <CycleCountsDashboard />,
                    },
                    {
                        path: 'create',
                        handle: { title: 'Create Cycle Count' },
                        element: <CreateCycleCount />,
                    },
                    {
                        path: ':id',
                        handle: { title: 'Cycle Count Detail' },
                        element: <CycleCountDetail />,
                    },
                    {
                        path: ':id/perform',
                        handle: { title: 'Perform Cycle Count' },
                        element: <CycleCountPerform />,
                    },
                ],
            },
        ],
    },
];

export default protectedRoutes;
